import { useEffect, useState, ReactNode, FC } from 'react';
import { useLocation } from 'react-router-dom';
import WalletManager from '@/services/wallet/wallet-manager';
import useWalletStore from '@/store/walletStore';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * AuthGuard - Protege las rutas de React
 * Si no hay una sesión activa o clave privada en memoria, 
 * redirige al usuario a la aplicación clásica para autenticarse.
 */
const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const walletManager = WalletManager.getInstance();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      console.log('[AuthGuard] Verificando sesión...');

      // 1. ¿Ya está desbloqueada en memoria?
      if (walletManager.isUnlocked()) {
        const addr = walletManager.getActiveAddress();
        if (addr) {
          useWalletStore.getState().setAddress(addr);
          useWalletStore.getState().setIsConnected(true);
        }
        setIsAuthorized(true);
        setIsChecking(false);
        return;
      }

      // 2. ¿Hay una clave privada transferida en sessionStorage?
      const transferredPK = sessionStorage.getItem('wallet_privatekey');
      const transferredMnemonic = sessionStorage.getItem('wallet_mnemonic');
      if (transferredPK) {
        try {
          walletManager.loadFromPrivateKey(transferredPK, transferredMnemonic || undefined);
          const addr = walletManager.getActiveAddress();
          if (addr) {
            useWalletStore.getState().setAddress(addr);
            useWalletStore.getState().setIsConnected(true);
          }
          setIsAuthorized(true);
          setIsChecking(false);
          return;
        } catch (e) {
          console.error('[AuthGuard] Error al cargar PK transferida:', e);
        }
      }

      // 3. ¿Hay una contraseña transferida en sessionStorage? (Desde el login clásico)
      const transferredPassword = sessionStorage.getItem('react_wallet_password');
      if (transferredPassword) {
        try {
          await walletManager.unlock(transferredPassword);
          const addr = walletManager.getActiveAddress();
          if (addr) {
            useWalletStore.getState().setAddress(addr);
            useWalletStore.getState().setIsConnected(true);
          }
          // Limpiar contraseña de la sesión por seguridad
          sessionStorage.removeItem('react_wallet_password');
          setIsAuthorized(true);
          setIsChecking(false);
          return;
        } catch (e) {
          console.error('[AuthGuard] Error al desbloquear con contraseña transferida:', e);
        }
      }

      // 4. ¿Hay un token de sesión genérico pero no tenemos la PK?
      // Esto puede pasar al refrescar la página. Sin la PK o pass, no podemos operar.

      // Si llegamos aquí, no está autorizado
      console.log('[AuthGuard] No autorizado, redirigiendo a la app base');
      setIsAuthorized(false);
      setIsChecking(false);
    };

    checkAuth();
  }, [location]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-main">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="text-text-secondary animate-pulse">Sincronizando sesión...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    // Redirigir a la raíz (Vanilla JS App)
    window.location.href = '/';
    return null;
  }

  return <>{children}</>;
};

export default AuthGuard;
